import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart } from 'lucide-react';
import AssetLogo from './AssetLogo';
import PriceChange from './PriceChange';
import type { MarketTick } from '../hooks/useMarketWS';

// Only FOREX and CC symbols are genuinely streamed via WebSocket.
// JSE, US, LSE, INDX etc. are polled — show updated prices but no LIVE badge.
const isStreamingSymbol = (symbol: string) =>
  symbol.endsWith('.FOREX') || symbol.endsWith('.CC');

interface MarketAssetRowProps {
  symbol: string;
  name: string;
  price: number | null;
  change: number;
  volume?: string;
  currencySymbol: string;
  previousClose?: number;
  onBuy?: (symbol: string, price: number) => void;
  /** Live tick from the page-level shared WebSocket — avoids per-row connections. */
  liveTick?: MarketTick;
}

const MarketAssetRow: React.FC<MarketAssetRowProps> = ({
  symbol,
  name,
  price,
  change,
  volume,
  currencySymbol,
  previousClose,
  onBuy,
  liveTick,
}) => {
  const navigate = useNavigate();

  const isStreaming = isStreamingSymbol(symbol);
  const isLive = isStreaming && !!liveTick;

  // Use live price if available, otherwise fall back to static price
  const displayPrice = isLive && liveTick.price ? liveTick.price : price;

  // Calculate change percentage from previous close for accuracy
  const displayChange = isLive && liveTick.price && previousClose
    ? ((liveTick.price - previousClose) / previousClose) * 100
    : change;

  return (
    <tr
      className="border-t border-slate-700/50 hover:bg-slate-700/30 cursor-pointer transition-colors group"
      onClick={() => navigate(`/asset/${symbol}`)}
    >
      <td className="py-3">
        <AssetLogo symbol={symbol} name={name} size="sm" />
      </td>
      <td className="py-3">
        <div className="flex items-center gap-2">
          <span className="font-mono font-medium text-teal-400">
            {symbol.split('.')[0]}
          </span>
          {isLive && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-[10px] font-semibold text-emerald-400 uppercase tracking-wide">
              <span className="w-1 h-1 bg-emerald-500 rounded-full animate-pulse" />
              LIVE
            </span>
          )}
        </div>
      </td>
      <td className="py-3 text-sm text-slate-300">{name}</td>
      <td className="py-3 text-right font-mono">
        {displayPrice != null
          ? `${currencySymbol}${displayPrice.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: symbol.endsWith('.FOREX') ? 4 : 2,
            })}`
          : '—'}
      </td>
      <td className="py-3 text-right">
        <div className="flex justify-end">
          <PriceChange value={displayChange || 0} size="sm" />
        </div>
      </td>
      <td className="py-3 text-right text-sm text-slate-400">{volume || 'N/A'}</td>
      <td className="py-3 text-right">
        {onBuy && displayPrice != null && (
          <button
            onClick={(e) => { e.stopPropagation(); onBuy(symbol, displayPrice); }}
            className="opacity-0 group-hover:opacity-100 flex items-center gap-1 px-2.5 py-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-semibold rounded transition-all ml-auto"
          >
            <ShoppingCart size={12} />
            Buy
          </button>
        )}
      </td>
    </tr>
  );
};

export default MarketAssetRow;
